/**
 * App Detail Page — 앱 상세·실행
 *
 * 앱 카드의 "열기" 버튼 → 이 페이지로 이동
 * - 좌: 미리보기 (목업 데이터 샌드박스) / 사용 방법 탭
 * - 우: 앱 실행하기 / 정보 패널
 *
 * 미리보기는 목업 데이터로 표시됩니다.
 * 실행하면 내 권한으로 조회한 실제 데이터가 보여요.
 */

import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Topbar } from '../components/layout';
import { Badge, Button, useToast } from '../components/ui';
import './AppDetailPage.css';

// ── 타입 ──────────────────────────────────────────────────────────────────────

type ThumbType = 'chart' | 'doc' | 'form';

interface AppItem {
  id: string;
  name: string;
  desc: string;
  kind: string;
  scope: string;
  cat: string;
  catLabel: string;
  owner: string;
  org: string;
  bot: string;
  runs: number;
  rating: string;
  thumb: ThumbType;
  tint: string;
  howto: string;
}

// ── 앱 목업 데이터 (AppsPage 와 공유) ──────────────────────────────────────────

const APP_MAP: Record<string, AppItem> = {
  a1: {
    id: 'a1', name: 'AWS 월간 비용 리포트',
    desc: '워킹온 DB의 고객사별 AWS 청구 데이터를 월 단위 차트와 표로 정리합니다.',
    kind: 'HTML 대시보드', scope: '에티버스 테넌트', cat: 'data', catLabel: '데이터·리포트',
    owner: '김수연', org: '에티버스', bot: '워킹온 DB 커넥터',
    runs: 342, rating: '4.8', thumb: 'chart', tint: '#EEECFC',
    howto: '실행하면 워킹온 DB에서 당월 청구 데이터를 자동으로 가져옵니다.\n고객사 필터와 날짜 범위를 선택하면 해당 기간 데이터만 표시됩니다.',
  },
  a2: {
    id: 'a2', name: 'EC2 Right-sizing 계산기',
    desc: '인스턴스 사용률을 입력하면 권장 타입과 월 절감 예상액을 계산합니다.',
    kind: '웹앱', scope: '에티버스 테넌트', cat: 'cloud', catLabel: '클라우드',
    owner: '유혜경', org: '에티버스', bot: '웹검색',
    runs: 157, rating: '4.9', thumb: 'chart', tint: '#E6F4EC',
    howto: '현재 인스턴스 타입과 평균 CPU·메모리 사용률을 입력하세요.\nAWS 최신 가격 기준으로 권장 타입과 절감액을 계산합니다.',
  },
  a3: {
    id: 'a3', name: '회의록 액션아이템 정리기',
    desc: '회의록을 붙여넣으면 담당자·기한별 할 일 표를 만들어 Slack으로 보냅니다.',
    kind: '웹앱', scope: '에티버스이피에이', cat: 'prod', catLabel: '업무생산성',
    owner: '박지훈', org: '에티버스이피에이', bot: '기본 채팅',
    runs: 211, rating: '4.7', thumb: 'form', tint: '#E6F4EC',
    howto: '회의록 텍스트를 붙여넣고 "정리하기" 버튼을 클릭하세요.\nAI가 담당자·기한·내용을 추출해 표로 정리하고, Slack 알림 발송 여부를 선택할 수 있습니다.',
  },
  a4: {
    id: 'a4', name: 'Support-on 장애 접수 폼',
    desc: '증상·영향 범위·첨부를 받아 Support-on 가이드 기준으로 1차 분류 결과를 보여줍니다.',
    kind: '웹앱', scope: '에티버스 테넌트', cat: 'support', catLabel: '기술지원',
    owner: '엄재한', org: '이테크 시스템', bot: 'Support-on 가이드',
    runs: 189, rating: '4.6', thumb: 'form', tint: '#E8F0FC',
    howto: '장애 증상과 영향 범위를 입력하면 Support-on 가이드 기준으로 우선순위와 담당팀을 자동 분류합니다.',
  },
  a5: {
    id: 'a5', name: 'MSP 제안서 표지 생성기',
    desc: '고객사명과 사업명을 넣으면 회사 양식의 제안서 표지를 만들어 PDF로 저장합니다.',
    kind: '웹앱', scope: '고객사 2곳', cat: 'sales', catLabel: '영업지원',
    owner: '엄재한', org: '이테크 시스템', bot: '기본 채팅',
    runs: 128, rating: '4.6', thumb: 'doc', tint: '#E8F0FC',
    howto: '고객사명, 사업명, 담당자, 날짜를 입력하면 회사 양식에 맞는 제안서 표지가 생성됩니다.\n다운로드 버튼으로 PDF 저장 가능합니다.',
  },
  a6: {
    id: 'a6', name: '고객사 미팅 사전 브리프',
    desc: '워킹온 CRM의 딜·이력 정보를 한 장짜리 미팅 브리프로 정리합니다.',
    kind: 'HTML 문서', scope: '지정 사용자', cat: 'sales', catLabel: '영업지원',
    owner: '김수연', org: '에티버스', bot: '워킹온 DB 커넥터',
    runs: 103, rating: '4.4', thumb: 'doc', tint: '#FDF1DC',
    howto: '고객사명을 입력하면 CRM에서 최근 딜·이력·담당자 정보를 가져와 미팅 브리프를 자동 생성합니다.',
  },
  a7: {
    id: 'a7', name: 'AI-DLC 요구사항 체크리스트',
    desc: 'AI-DLC 문서 가이드 기준으로 단계별 산출물 누락 여부를 점검합니다.',
    kind: 'HTML 문서', scope: '이테크 시스템', cat: 'dev', catLabel: '개발',
    owner: '엄재한', org: '이테크 시스템', bot: 'AI DLC 문서 가이드',
    runs: 96, rating: '4.5', thumb: 'doc', tint: '#FDF1DC',
    howto: '프로젝트명을 입력하고 단계를 선택하면 해당 단계에서 필요한 산출물 체크리스트가 표시됩니다.',
  },
  a8: {
    id: 'a8', name: '주간 IT 뉴스 브리핑 카드',
    desc: '웹크롤링으로 모은 네이버 뉴스를 주제별 카드로 요약해 보여줍니다.',
    kind: 'HTML 문서', scope: '에티버스 테넌트', cat: 'data', catLabel: '데이터·리포트',
    owner: '이서윤', org: '에티버스', bot: '웹크롤링 워크플로우',
    runs: 74, rating: '4.3', thumb: 'chart', tint: '#F3EEFB',
    howto: '매주 월요일 오전 9시에 자동 업데이트됩니다.\n수동으로 갱신하려면 "새로고침" 버튼을 클릭하세요.',
  },
};

// ── 앱별 미리보기 컴포넌트 ───────────────────────────────────────────────────

function PreviewA1() {
  return (
    <div className="apd-preview-content">
      <div className="apd-preview-title-row">
        <strong className="apd-preview-h">2026년 10월 AWS 비용 리포트</strong>
        <span className="apd-preview-date">기준: 10.22 워킹온 DB 동기화</span>
      </div>
      {/* KPI 카드 */}
      <div className="apd-kpi-grid">
        <div className="apd-kpi-card">
          <span>이번 달 청구액</span>
          <strong>₩48.2M</strong>
          <em className="apd-kpi-up">전월 대비 +6.4%</em>
        </div>
        <div className="apd-kpi-card">
          <span>고객사</span>
          <strong>37곳</strong>
          <em>신규 2곳</em>
        </div>
        <div className="apd-kpi-card">
          <span>절감 기회</span>
          <strong>₩3.1M</strong>
          <em>RI·SP 미적용 12건</em>
        </div>
      </div>
      {/* 바 차트 */}
      <div>
        <span className="apd-chart-label">월별 청구액 (백만 원)</span>
        <div className="apd-bar-chart">
          {[
            { month: '5월',  val: 39.8, h: 96,  color: '#C9C4F3', bold: false },
            { month: '6월',  val: 41.2, h: 100, color: '#C9C4F3', bold: false },
            { month: '7월',  val: 43.5, h: 106, color: '#C9C4F3', bold: false },
            { month: '8월',  val: 44.1, h: 108, color: '#C9C4F3', bold: false },
            { month: '9월',  val: 45.3, h: 110, color: '#A49CEB', bold: false },
            { month: '10월', val: 48.2, h: 118, color: '#4F45C9', bold: true  },
          ].map((b) => (
            <div key={b.month} className="apd-bar-col">
              <span className={`apd-bar-val${b.bold ? ' apd-bar-val--bold' : ''}`}>{b.val}</span>
              <span className="apd-bar" style={{ height: b.h, background: b.color }} />
              <span className="apd-bar-month">{b.month}</span>
            </div>
          ))}
        </div>
      </div>
      {/* 테이블 */}
      <table className="apd-table">
        <thead>
          <tr><th>고객사</th><th>청구액</th><th>전월 대비</th></tr>
        </thead>
        <tbody>
          <tr><td>고객사 A</td><td>₩9.4M</td><td className="apd-up">+12.1%</td></tr>
          <tr><td>고객사 B</td><td>₩7.8M</td><td className="apd-up">+3.0%</td></tr>
          <tr><td>고객사 C</td><td>₩5.2M</td><td className="apd-down">−1.8%</td></tr>
          <tr><td>고객사 D</td><td>₩4.7M</td><td className="apd-up">+8.5%</td></tr>
          <tr><td>고객사 E</td><td>₩3.9M</td><td>±0.0%</td></tr>
        </tbody>
      </table>
    </div>
  );
}

function PreviewA2() {
  return (
    <div className="apd-preview-content">
      <div className="apd-preview-title-row">
        <strong className="apd-preview-h">EC2 Right-sizing 결과</strong>
        <span className="apd-preview-date">2026-10-22 분석</span>
      </div>
      <div className="apd-kpi-grid">
        <div className="apd-kpi-card">
          <span>현재 월 비용</span>
          <strong>$2,840</strong>
          <em>12개 인스턴스</em>
        </div>
        <div className="apd-kpi-card">
          <span>권장 후 월 비용</span>
          <strong>$1,920</strong>
          <em className="apd-kpi-up">$920 절감</em>
        </div>
        <div className="apd-kpi-card">
          <span>절감률</span>
          <strong>32.4%</strong>
          <em>연간 $11,040</em>
        </div>
      </div>
      <table className="apd-table">
        <thead>
          <tr><th>현재 타입</th><th>평균 CPU</th><th>권장 타입</th><th>월 절감</th></tr>
        </thead>
        <tbody>
          <tr><td>m5.2xlarge</td><td>18%</td><td>m5.large</td><td className="apd-up">$310</td></tr>
          <tr><td>r5.4xlarge</td><td>22%</td><td>r5.xlarge</td><td className="apd-up">$480</td></tr>
          <tr><td>c5.xlarge</td><td>67%</td><td>c5.xlarge</td><td>유지</td></tr>
          <tr><td>t3.large</td><td>12%</td><td>t3.medium</td><td className="apd-up">$55</td></tr>
        </tbody>
      </table>
    </div>
  );
}

function PreviewA3() {
  return (
    <div className="apd-preview-content">
      <strong className="apd-preview-h">회의록 분석 결과</strong>
      <p style={{ fontSize: 13, color: '#5F6478', marginTop: 4 }}>2026-10-22 주간 기획 회의 · 참석자 5명</p>
      <table className="apd-table" style={{ marginTop: 12 }}>
        <thead>
          <tr><th>담당자</th><th>액션 아이템</th><th>기한</th></tr>
        </thead>
        <tbody>
          <tr><td>김수연</td><td>AWS 비용 리포트 v1.1 수정</td><td>10/25</td></tr>
          <tr><td>박지훈</td><td>고객사 A 미팅 브리프 준비</td><td>10/24</td></tr>
          <tr><td>유혜경</td><td>EC2 계산기 테스트 완료 보고</td><td>10/26</td></tr>
          <tr><td>엄재한</td><td>Support-on 폼 배포 확인</td><td>10/23</td></tr>
        </tbody>
      </table>
      <div className="apd-slack-badge">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.84 9.18a19.79 19.79 0 01-3.07-8.67A2 2 0 012.75 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 9.72a16 16 0 006.29 6.29l1.08-1.08a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"></path></svg>
        Slack #기획팀 채널로 전송됨
      </div>
    </div>
  );
}

function PreviewForm({ app }: { app: AppItem }) {
  return (
    <div className="apd-preview-content">
      <strong className="apd-preview-h">{app.name}</strong>
      <p style={{ fontSize: 13, color: '#5F6478', marginTop: 4 }}>{app.desc}</p>
      <div className="apd-form-preview">
        <div className="apd-form-field">
          <label>제목 / 이름</label>
          <div className="apd-form-input apd-form-input--filled">샘플 입력값</div>
        </div>
        <div className="apd-form-field">
          <label>세부 내용</label>
          <div className="apd-form-textarea">목업 데이터입니다. 실제 실행 시 입력한 내용이 여기에 표시됩니다.</div>
        </div>
        <div className="apd-form-field">
          <label>분류</label>
          <div className="apd-form-select">자동 분류 — 보통 (P2)</div>
        </div>
        <button className="apd-form-submit" disabled>처리 결과 보기</button>
      </div>
    </div>
  );
}

function PreviewDoc({ app }: { app: AppItem }) {
  return (
    <div className="apd-preview-content">
      <strong className="apd-preview-h">{app.name}</strong>
      <p style={{ fontSize: 13, color: '#5F6478', marginTop: 4 }}>생성일: 2026-10-22 · {app.owner} ({app.org})</p>
      <div className="apd-doc-preview">
        <div className="apd-doc-section">
          <div className="apd-doc-section-title" />
          <div className="apd-doc-line" style={{ width: '100%' }} />
          <div className="apd-doc-line" style={{ width: '92%' }} />
          <div className="apd-doc-line" style={{ width: '85%' }} />
        </div>
        <div className="apd-doc-section">
          <div className="apd-doc-section-title" />
          <div className="apd-doc-line" style={{ width: '100%' }} />
          <div className="apd-doc-line" style={{ width: '78%' }} />
          <div className="apd-doc-line" style={{ width: '88%' }} />
          <div className="apd-doc-line" style={{ width: '60%' }} />
        </div>
      </div>
      <p style={{ fontSize: 12, color: '#9AA0B4', marginTop: 8 }}>
        미리보기는 목업으로 표시됩니다. 실행하면 실제 데이터가 채워집니다.
      </p>
    </div>
  );
}

function AppPreview({ app }: { app: AppItem }) {
  if (app.id === 'a1') return <PreviewA1 />;
  if (app.id === 'a2') return <PreviewA2 />;
  if (app.id === 'a3') return <PreviewA3 />;
  if (app.thumb === 'form') return <PreviewForm app={app} />;
  return <PreviewDoc app={app} />;
}

// ── 컴포넌트 ──────────────────────────────────────────────────────────────────

export function AppDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<'preview' | 'howto'>('preview');
  const [running, setRunning] = useState(false);

  const app = id ? APP_MAP[id] : null;

  if (!app) {
    return (
      <>
        <Topbar title="앱 상세" />
        <div className="page-content">
          <div className="page-content-inner" style={{ textAlign: 'center', paddingTop: 80 }}>
            <p>앱을 찾을 수 없습니다.</p>
            <Button onClick={() => navigate('/library')} style={{ marginTop: 16 }}>← 라이브러리로 돌아가기</Button>
          </div>
        </div>
      </>
    );
  }

  const handleRun = () => setRunning(true);
  const runLabel = app.kind === '웹앱' ? '실행하기' : '열기';

  return (
    <>
      {/* ── 전체화면 실행 모달 ── */}
      {running && (
        <div className="apd-run-overlay" role="dialog" aria-modal="true" aria-label={`${app.name} 실행 중`}>
          <div className="apd-run-topbar">
            <div className="apd-run-topbar-left">
              <span className="apd-run-status-dot" />
              <span className="apd-run-status-text">실행 중</span>
              <span className="apd-run-topbar-name">{app.name}</span>
              <span className="apd-run-topbar-ver">v1.2</span>
            </div>
            <div className="apd-run-topbar-right">
              <span className="apd-run-sandbox-badge">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 018 0v4" />
                </svg>
                샌드박스 · 외부 네트워크 차단
              </span>
              <button type="button" className="apd-run-close" onClick={() => setRunning(false)} aria-label="닫기">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>
          <div className="apd-run-body">
            <AppPreview app={app} />
          </div>
          <div className="apd-run-footer">
            목업 데이터로 실행 중입니다. 실제 환경에서는 내 권한으로 조회한 데이터가 표시됩니다.
          </div>
        </div>
      )}
      {/* Topbar: leading에 뒤로가기 */}
      <Topbar
        title={app.name}
        leading={
          <button type="button" className="apd-back-btn" onClick={() => navigate('/library')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M19 12H5M12 5l-7 7 7 7" /></svg>
          </button>
        }
      />

      <div className="page-content">
        <div className="page-content-inner apd-container">

          {/* 브레드크럼 */}
          <nav className="apd-breadcrumb" aria-label="경로">
            <Link to="/library">라이브러리</Link>
            <span>/</span>
            <span>{app.catLabel}</span>
            <span>/</span>
            <span className="apd-breadcrumb-current">{app.name}</span>
          </nav>

          {/* 앱 헤더 */}
          <header className="apd-header">
            <div className="apd-header-icon" style={{ background: '#EEECFC' }}>
              {app.thumb === 'chart' && (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#4F45C9" strokeWidth="2" strokeLinecap="round">
                  <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
                </svg>
              )}
              {app.thumb === 'doc' && (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#4F45C9" strokeWidth="2" strokeLinecap="round">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
              )}
              {app.thumb === 'form' && (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#4F45C9" strokeWidth="2" strokeLinecap="round">
                  <rect x="2" y="3" width="20" height="14" rx="2" />
                  <path d="M8 21h8M12 17v4" />
                </svg>
              )}
            </div>
            <div className="apd-header-body">
              <h1 className="apd-header-name">{app.name}</h1>
              <p className="apd-header-desc">{app.desc}</p>
              <div className="apd-header-meta">
                <span className="apd-tag-kind">{app.kind}</span>
                <Badge color="success">승인됨 · v1.2</Badge>
                <span>{app.runs.toLocaleString()}회 실행</span>
                <span>평점 {app.rating}</span>
                <span>{app.owner} · {app.org}</span>
              </div>
            </div>
          </header>

          {/* 본문 2-컬럼 */}
          <div className="apd-body">
            {/* ── 좌: 탭 + 미리보기 ── */}
            <div className="apd-main">
              {/* 탭 */}
              <div className="apd-tabs" role="tablist">
                <button
                  type="button" role="tab"
                  aria-selected={activeTab === 'preview'}
                  className={`apd-tab${activeTab === 'preview' ? ' apd-tab--active' : ''}`}
                  onClick={() => setActiveTab('preview')}
                >
                  미리보기
                </button>
                <button
                  type="button" role="tab"
                  aria-selected={activeTab === 'howto'}
                  className={`apd-tab${activeTab === 'howto' ? ' apd-tab--active' : ''}`}
                  onClick={() => setActiveTab('howto')}
                >
                  사용 방법
                </button>
              </div>

              {activeTab === 'preview' && (
                <figure className="apd-sandbox">
                  {/* 샌드박스 상단 바 */}
                  <div className="apd-sandbox-bar">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <rect x="5" y="11" width="14" height="10" rx="2" />
                      <path d="M8 11V7a4 4 0 018 0v4" />
                    </svg>
                    <span>샌드박스 실행 · 외부 네트워크 차단 · 워킹온 DB 읽기 전용</span>
                    <span className="apd-sandbox-path">apps/{app.id} · v1.2</span>
                  </div>

                  {/* 미리보기 내용 */}
                  <AppPreview app={app} />

                  {/* 하단 캡션 */}
                  <figcaption className="apd-sandbox-caption">
                    미리보기는 목업 데이터로 표시됩니다. 실행하면 내 권한으로 조회한 실제 데이터가 보여요.
                  </figcaption>
                </figure>
              )}

              {activeTab === 'howto' && (
                <div className="apd-howto">
                  {app.howto.split('\n').map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
                </div>
              )}
            </div>

            {/* ── 우: 사이드바 ── */}
            <aside className="apd-aside">
              {/* 실행 패널 */}
              <section className="apd-aside-panel">
                <Button
                  className="apd-run-btn"
                  onClick={handleRun}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round">
                    <path d="M7 5l12 7-12 7z" />
                  </svg>
                  {runLabel}
                </Button>
                <button type="button" className="apd-clone-btn">
                  내 워크스페이스로 복제
                </button>
                <div className="apd-aside-links">
                  <button type="button" className="apd-aside-link" onClick={() => navigate('/chat')}>
                    원본 챗봇 대화 열기
                  </button>
                  <button type="button" className="apd-aside-link" onClick={() => {
                    const favs: string[] = JSON.parse(localStorage.getItem('library_favorites') || '[]');
                    const isFav = favs.includes(app.id);
                    const next = isFav ? favs.filter((f: string) => f !== app.id) : [...favs, app.id];
                    localStorage.setItem('library_favorites', JSON.stringify(next));
                    showToast('success', isFav ? '즐겨찾기에서 제거됐습니다.' : '즐겨찾기에 추가됐습니다.');
                  }}>
                    즐겨찾기
                  </button>
                </div>
              </section>

              {/* 앱 정보 */}
              <section className="apd-aside-panel">
                <h3 className="apd-aside-title">앱 정보</h3>
                <dl className="apd-info-list">
                  <div><dt>만든 사람</dt><dd>{app.owner} ({app.org})</dd></div>
                  <div><dt>원본 챗봇</dt><dd>{app.bot}</dd></div>
                  <div><dt>공개 범위</dt><dd>{app.scope}</dd></div>
                  <div><dt>종류</dt><dd>{app.kind}</dd></div>
                  <div><dt>카테고리</dt><dd>{app.catLabel}</dd></div>
                  <div><dt>버전</dt><dd>v1.2 (2026.10.18 게시)</dd></div>
                  <div><dt>총 실행</dt><dd>{app.runs.toLocaleString()}회</dd></div>
                  <div><dt>평점</dt><dd>⭐ {app.rating} / 5.0</dd></div>
                </dl>
              </section>
            </aside>
          </div>

        </div>
      </div>
    </>
  );
}

export default AppDetailPage;
